import { useEffect, useCallback } from 'react';
import { useDialog } from 'components/core/Dialog/common/DialogContext';
import { FormattedMessage } from 'intl';
import { DialogTranslationKeys } from 'intl/keys';

import { FormName } from 'presentation/forms';
import { getUiStoreState, useIsFormEditing } from 'store/ui';

export function useFormCleanupOnUnmount(form: FormName): void {
  useEffect(() => {
    return (): void => {
      getUiStoreState().actions.setFormEditing(form, false);
    };
  }, [form]);
}

export function useCancelConfirmation(form: FormName): () => Promise<{
  cancelConfirmed: boolean;
}> {
  const createDialog = useDialog();
  const isFormEditing = useIsFormEditing(form);

  const showCancelConfirmationDialog = useCallback(async (): Promise<{
    cancelConfirmed: boolean;
  }> => {
    return createDialog({
      catchOnCancel: true,
      title: (
        <FormattedMessage id={DialogTranslationKeys.confirmationDiscardChangesTitleWeb} />
      ),
      description: (
        <span>
          <FormattedMessage id={DialogTranslationKeys.confirmationDiscardChangesWeb} />
        </span>
      ),
    })
      .then(() => ({ cancelConfirmed: true }))
      .catch(() => ({ cancelConfirmed: false }));
  }, [createDialog]);

  const confirmCancel = useCallback(async () => {
    const isFormClean = !isFormEditing;
    if (isFormClean) {
      return Promise.resolve({ cancelConfirmed: true });
    } else {
      return showCancelConfirmationDialog();
    }
  }, [isFormEditing, showCancelConfirmationDialog]);

  return confirmCancel;
}
